import { useLayoutEffect, useState } from 'react'
import { getTelegramWebApp } from '../telegram'
import type { TelegramSafeAreaInset } from '../telegram'

/** Min top inset for mobile Telegram fullscreen if API top values are unrealistically small. */
export const MOBILE_FULLSCREEN_FALLBACK_TOP_PX = 96
/** If max(raw content top, raw safe top) is below this, use {@link MOBILE_FULLSCREEN_FALLBACK_TOP_PX}. */
const MOBILE_FULLSCREEN_FALLBACK_RAW_MAX = 72

const TG_EVENTS = ['safeAreaChanged', 'contentSafeAreaChanged', 'fullscreenChanged', 'viewportChanged'] as const

function insetNum(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseFloat(v)
    if (Number.isFinite(n) && n >= 0) return n
  }
  return 0
}

function readInset(src: TelegramSafeAreaInset | null | undefined) {
  if (!src || typeof src !== 'object') {
    return { top: 0, bottom: 0, left: 0, right: 0 }
  }
  return {
    top: insetNum(src.top),
    bottom: insetNum(src.bottom),
    left: insetNum(src.left),
    right: insetNum(src.right),
  }
}

function isMobileByUa() {
  return /Android|iPhone|iPad|iPod/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '')
}

export type TelegramSafeArea = {
  top: number
  bottom: number
  left: number
  right: number
  contentTop: number
  contentBottom: number
  contentLeft: number
  contentRight: number
  platform: string
  isFullscreen: boolean
  isMobile: boolean
  isDesktop: boolean
  /** True when {@link MOBILE_FULLSCREEN_FALLBACK_TOP_PX} is used for top inset. */
  fallbackApplied: boolean
  appSafeTop: number
  appSafeBottom: number
  appSafeLeft: number
  appSafeRight: number
  viewportHeight: number
  viewportStableHeight: number
  rawContentTop: number
  rawSafeTop: number
}

function readViewport(wa: ReturnType<typeof getTelegramWebApp>, key: 'viewportHeight' | 'viewportStableHeight'): number {
  if (!wa) return 0
  const v = (wa as { viewportHeight?: number; viewportStableHeight?: number })[key]
  return insetNum(v)
}

function buildSnapshot(wa: ReturnType<typeof getTelegramWebApp>): TelegramSafeArea {
  const platform = (wa && typeof wa.platform === 'string' ? wa.platform : 'unknown') || 'unknown'
  const pl = platform.toLowerCase()
  const isDesktop = pl === 'tdesktop' || pl === 'macos' || pl === 'web'
  const isTelegramMobile = pl === 'android' || pl === 'ios' || pl === 'android_x'
  const isMobile = isTelegramMobile || isMobileByUa()

  const s = readInset(wa?.safeAreaInset)
  const c = readInset(wa?.contentSafeAreaInset)
  const rawContentTop = c.top
  const rawSafeTop = s.top
  const isFullscreen = Boolean(wa?.isFullscreen)
  const viewportHeight = readViewport(wa, 'viewportHeight')
  const viewportStableHeight = readViewport(wa, 'viewportStableHeight')

  const maxRaw = Math.max(rawContentTop, rawSafeTop)
  const shouldApplyMobileFullscreenFallback =
    isMobile && !isDesktop && isFullscreen && maxRaw < MOBILE_FULLSCREEN_FALLBACK_RAW_MAX

  const appSafeTop = shouldApplyMobileFullscreenFallback ? MOBILE_FULLSCREEN_FALLBACK_TOP_PX : maxRaw
  const fallbackApplied = shouldApplyMobileFullscreenFallback

  const appSafeBottom = Math.max(c.bottom, s.bottom)
  const appSafeLeft = Math.max(c.left, s.left)
  const appSafeRight = Math.max(c.right, s.right)

  return {
    top: s.top,
    bottom: s.bottom,
    left: s.left,
    right: s.right,
    contentTop: c.top,
    contentBottom: c.bottom,
    contentLeft: c.left,
    contentRight: c.right,
    platform,
    isFullscreen,
    isMobile,
    isDesktop,
    fallbackApplied,
    appSafeTop,
    appSafeBottom,
    appSafeLeft,
    appSafeRight,
    viewportHeight,
    viewportStableHeight,
    rawContentTop,
    rawSafeTop,
  }
}

function applyRootCssVars(s: TelegramSafeArea) {
  if (typeof document === 'undefined') return
  const el = document.documentElement
  const px = (n: number) => `${n}px`

  el.style.setProperty('--tg-safe-top', px(s.top))
  el.style.setProperty('--tg-safe-bottom', px(s.bottom))
  el.style.setProperty('--tg-content-safe-top', px(s.contentTop))
  el.style.setProperty('--tg-content-safe-bottom', px(s.contentBottom))
  el.style.setProperty('--tg-safe-area-top', px(s.top))
  el.style.setProperty('--tg-safe-area-bottom', px(s.bottom))
  el.style.setProperty('--tg-safe-area-left', px(s.left))
  el.style.setProperty('--tg-safe-area-right', px(s.right))
  el.style.setProperty('--tg-content-safe-area-top', px(s.contentTop))
  el.style.setProperty('--tg-content-safe-area-bottom', px(s.contentBottom))
  el.style.setProperty('--tg-content-safe-area-left', px(s.contentLeft))
  el.style.setProperty('--tg-content-safe-area-right', px(s.contentRight))
  // PWA has no Telegram insets. Keep the browser's safe area live so iOS
  // status bars/notches remain protected, including after device rotation.
  // Use max rather than addition: both sources describe the same screen edge.
  el.style.setProperty('--app-safe-top', `max(env(safe-area-inset-top, 0px), ${px(s.appSafeTop)})`)
  el.style.setProperty('--app-safe-bottom', `max(env(safe-area-inset-bottom, 0px), ${px(s.appSafeBottom)})`)
  el.style.setProperty('--app-safe-left', `max(env(safe-area-inset-left, 0px), ${px(s.appSafeLeft)})`)
  el.style.setProperty('--app-safe-right', `max(env(safe-area-inset-right, 0px), ${px(s.appSafeRight)})`)
}

export function useTelegramSafeArea(): TelegramSafeArea {
  const [state, setState] = useState<TelegramSafeArea>(() => buildSnapshot(getTelegramWebApp()))

  useLayoutEffect(() => {
    const update = () => {
      const next = buildSnapshot(getTelegramWebApp())
      applyRootCssVars(next)
      setState(next)
      if (import.meta.env.DEV) {
        console.debug('[tg-safe-area]', {
          platform: next.platform,
          isFullscreen: next.isFullscreen,
          isMobile: next.isMobile,
          isDesktop: next.isDesktop,
          safeAreaInsetTop: next.rawSafeTop,
          contentSafeAreaInsetTop: next.rawContentTop,
          appSafeTop: next.appSafeTop,
          fallbackApplied: next.fallbackApplied,
          viewportHeight: next.viewportHeight,
          viewportStableHeight: next.viewportStableHeight,
        })
      }
    }

    update()

    const wa = getTelegramWebApp()
    const on = wa && typeof wa.onEvent === 'function' ? wa.onEvent.bind(wa) : null
    const off = wa && typeof wa.offEvent === 'function' ? wa.offEvent.bind(wa) : null

    if (on && off) {
      for (const ev of TG_EVENTS) {
        try {
          on(ev, update)
        } catch {
          // ignore
        }
      }
      return () => {
        for (const ev of TG_EVENTS) {
          try {
            off(ev, update)
          } catch {
            // ignore
          }
        }
      }
    }
    return
  }, [])

  return state
}
